import { BasePdfGenerator } from '../shared/BasePdfGenerator'
import { COLORS, PAGE, CONTENT_WIDTH } from '../shared/constants'
import { toPrintable } from '../shared/winAnsi'
import { documentBodyForDisplay } from '@/components/documents/documentBody'
import type { AuthoredDocumentPdfData } from './types'

/**
 * A chat-authored document as a PDF (MYAC-1162).
 *
 * The thinnest generator in the family on purpose: the body is one markdown string,
 * and `BasePdfGenerator.addMarkdownContent` already renders headings, lists, tables,
 * quotes and code blocks for four other reports. Anything this file did to the body
 * itself would be a second markdown renderer to keep in step with the screen's.
 *
 * Two things it must do that the base cannot do for it:
 *
 * `toPrintable` over the body — this is LLM-authored free text, so it is exactly the
 * content that carries arrows, en dashes and check marks, and ONE of those makes
 * jsPDF re-encode the whole string as 2-byte, which Helvetica draws as letter-spaced
 * garbage.
 *
 * `documentBodyForDisplay` — the same rule the screen applies. The chat opens a
 * document with an h1 repeating its own title, and this PDF already prints that title
 * on its cover line and in every running header. Three times would be worse than the
 * two the screen was fixed for.
 */
export class AuthoredDocumentPdfGenerator extends BasePdfGenerator {
  private data: AuthoredDocumentPdfData

  constructor(data: AuthoredDocumentPdfData) {
    super()
    this.data = data
    if (data.translate) {
      const translate = data.translate
      // Wrapped, like the other generators: a translated string reaches the page
      // through the same encoding gate as the document's own text.
      this.t = (key, params, plural) => toPrintable(translate(key, params, plural))
    }
  }

  generate(filename: string): void {
    const { title, markdown, statusLabel, revision } = this.data

    this.pdf.setFontSize(18)
    this.pdf.setFont('helvetica', 'bold')
    this.setColor(COLORS.text)
    for (const line of this.wrapText(toPrintable(title), CONTENT_WIDTH)) {
      this.pdf.text(line, PAGE.marginLeft, this.currentY)
      this.currentY += 8
    }

    this.pdf.setFontSize(9)
    this.pdf.setFont('helvetica', 'normal')
    this.setColor(COLORS.gray)
    this.pdf.text(
      toPrintable(`${statusLabel} · ${this.t('authoredDocuments.revision', { n: revision })}`),
      PAGE.marginLeft,
      this.currentY,
    )
    this.currentY += 10

    const body = documentBodyForDisplay(markdown ?? '', title)
    this.addMarkdownContent(toPrintable(body), PAGE.marginLeft, CONTENT_WIDTH)

    this.addHeadersAndFooters()
    this.pdf.save(filename)
  }

  protected addHeadersAndFooters(): void {
    const totalPages = this.pdf.getNumberOfPages()
    const { tenantName, projectName, title } = this.data
    const clip = (text: string) => (text.length > 30 ? `${text.slice(0, 27)}...` : text)

    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i)
      this.pdf.setFontSize(8)
      this.pdf.setFont('helvetica', 'normal')
      this.setColor(COLORS.gray)

      this.pdf.text(toPrintable(clip(tenantName)), PAGE.marginLeft, PAGE.marginTop)
      this.pdf.text(toPrintable(clip(projectName)), PAGE.width / 2, PAGE.marginTop, {
        align: 'center',
      })
      this.pdf.text(toPrintable(clip(title)), PAGE.width - PAGE.marginRight, PAGE.marginTop, {
        align: 'right',
      })

      this.pdf.setFontSize(9)
      this.pdf.text(
        `Page ${i} / ${totalPages}`,
        PAGE.width / 2,
        PAGE.height - PAGE.marginBottom + 5,
        {
          align: 'center',
        },
      )
    }

    // AI Act Art. 50(2). The screen carries `AiGeneratedTag`; a file that leaves the
    // app and gets mailed to an auditor needs to say it on every page.
    this.addAiGeneratedNotice()
  }
}
